import React, { useEffect, useState, useRef, createContext, useContext } from "react";
import * as Lucide from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  BRAND, NAV_LINKS, HERO, PATHWAYS, JOURNEY, CATEGORIES, ABOUT, BRANDS, PROGRAMS, TIERS,
  COVERAGE, COMING_SOON, FINAL_CTA, APP_STORES, FOOTER,
} from "@/content/landing";
import { useT, LanguageSwitcher } from "@/i18n/LanguageContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const { red: RED, gold: GOLD, charcoal: CHARCOAL } = BRAND.colors;

// ----------------------------------------------------------------
// Editable Content (CMS-lite) — admin overrides via /api/public/content
// ----------------------------------------------------------------
const ContentCtx = createContext({});
function useCV() {
  const overrides = useContext(ContentCtx);
  return (key, fallback) => {
    const v = overrides?.[key];
    return v && String(v).trim() ? v : fallback;
  };
}

const startLogin = (intendedRoute) => {
  const redirectUrl = window.location.origin + (intendedRoute || "/dashboard");
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const trackEvent = (event) => {
  axios.post(`${BACKEND}/api/metrics/event`, { event }).catch(() => {});
};

// Icon registry — turns string from content file into a real <Icon/>
function Icon({ name, ...props }) {
  const C = Lucide[name] || Lucide.Sparkles;
  return <C {...props} />;
}

// Smart route handler — `external` routes go directly (e.g. /vendor); the rest gate via login.
function handleCta(navigate, item) {
  if (item.external) {
    if (item.route.startsWith("http")) window.location.href = item.route;
    else navigate(item.route);
  } else {
    startLogin(item.route);
  }
}

// ============================================================
// HEADER
// ============================================================
function Header() {
  const navigate = useNavigate();
  const { t } = useT();
  const navKey = (href) => ({
    "#pathways":      "nav.pathways",
    "#opportunities": "nav.opportunities",
    "/vendor":        "nav.vendor",
    "#membership":    "nav.membership",
    "#coverage":      "nav.coverage",
  }[href]);

  return (
    <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-4">
        <a href="/" className="flex items-center gap-3 min-w-0" data-testid="brand-link">
          <img src={BRAND.logo} alt={BRAND.name} className="w-10 h-10 object-contain shrink-0" />
          <div className="leading-tight min-w-0">
            <div className="font-display font-black text-base tracking-tight truncate">{BRAND.name}</div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 truncate">{t("header.tagline")}</div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-zinc-700">
          {NAV_LINKS.map((n) => {
            const label = navKey(n.href) ? t(navKey(n.href)) : n.label;
            const isInternal = n.href.startsWith("/");
            const liveDot = n.live ? (
              <span className="relative inline-flex h-2 w-2 mr-1.5" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: RED }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: RED }} />
              </span>
            ) : null;
            const cls = n.live
              ? "inline-flex items-center font-bold hover:text-[#C8102E]"
              : "hover:text-[#C8102E]";
            return isInternal
              ? <button key={n.href} onClick={() => navigate(n.href)} className={cls} data-testid={n.live ? "header-nav-tv" : undefined}>{liveDot}{label}</button>
              : <a key={n.href} href={n.href} className={cls}>{liveDot}{label}</a>;
          })}
        </nav>
        <div className="flex items-center gap-3 shrink-0">
          <LanguageSwitcher />
          <button
            onClick={() => startLogin()}
            data-testid="header-login"
            className="px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
            style={{ background: RED }}
          >
            {t("header.signin")}
          </button>
        </div>
      </div>
    </header>
  );
}

// ============================================================
// HERO
// ============================================================
function Hero() {
  const navigate = useNavigate();
  const { t } = useT();
  const cv = useCV();
  // Map HERO.ctas by key → translation labels (preserves routing). Admin overrides take priority.
  const ctaLabel = (key) => {
    const fallback = ({
      earn:  t("hero.cta_earn"),
      start: t("hero.cta_start"),
      grow:  t("hero.cta_grow"),
    }[key] || key);
    return cv(`hero_cta_${key}`, fallback);
  };

  return (
    <section className="relative overflow-hidden border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none opacity-30">
        <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 60% 40%, ${GOLD}33 0%, transparent 50%)` }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 rise">
          {/* Audience pill — clarifies WHO this is for in one line */}
          <div className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: `${GOLD}22`, color: CHARCOAL }} data-testid="hero-audience">
            <Lucide.Users className="w-3.5 h-3.5" style={{ color: RED }} /> {cv("hero_audience_badge", t("hero.audience_badge"))}
          </div>

          <div className="tag mt-4" data-testid="hero-tag">
            <span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} />
            {cv("hero_tag", t("hero.tag"))}
          </div>
          <h1
            className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]"
            data-testid="hero-headline"
          >
            <span style={{ color: RED }}>{cv("hero_headline_1", t("hero.headline_1"))}</span><br />
            {cv("hero_headline_2", t("hero.headline_2"))}<br />
            {cv("hero_headline_3", t("hero.headline_3"))}
          </h1>
          <p className="mt-6 text-zinc-700 text-base sm:text-lg max-w-2xl" data-testid="hero-sub">
            {cv("hero_sub", t("hero.sub"))}
          </p>

          <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-ctas">
            {HERO.ctas.map((c) => (
              <button
                key={c.key}
                onClick={() => { trackEvent("hero_cta_" + c.key); handleCta(navigate, c); }}
                data-testid={c.testid}
                className={
                  c.primary
                    ? "inline-flex items-center gap-2 text-white px-6 py-3 font-semibold hover:opacity-90 transition-opacity"
                    : "inline-flex items-center gap-2 px-6 py-3 font-semibold border-2 hover:bg-zinc-100 transition-colors"
                }
                style={c.primary ? { background: RED } : { borderColor: CHARCOAL, color: CHARCOAL, background: "white" }}
              >
                {ctaLabel(c.key)} <Lucide.ArrowRight className="w-4 h-4" />
              </button>
            ))}
            <button
              onClick={() => { trackEvent("hero_cta_tv"); navigate("/tv"); }}
              data-testid="hero-cta-tv"
              className="inline-flex items-center gap-2 px-5 py-3 font-semibold bg-black text-white hover:bg-zinc-800 transition-colors"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: GOLD }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: GOLD }} />
              </span>
              {t("hero.cta_tv")} <Lucide.Play className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-zinc-600">
            <div className="flex items-center gap-2"><Lucide.Check className="w-4 h-4" style={{ color: RED }} /> {t("hero.check_1")}</div>
            <div className="flex items-center gap-2"><Lucide.Check className="w-4 h-4" style={{ color: RED }} /> {t("hero.check_2")}</div>
            <div className="flex items-center gap-2"><Lucide.Check className="w-4 h-4" style={{ color: RED }} /> {t("hero.check_3")}</div>
          </div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <PhoneMockup />
        </div>
      </div>
    </section>
  );
}

// ============================================================
// PHONE MOCKUP
// ============================================================
function PhoneMockup() {
  const cv = useCV();
  return (
    <div className="relative mx-auto" style={{ maxWidth: 280 }} data-testid="phone-mockup">
      <div className="relative rounded-[40px] p-3 shadow-2xl" style={{ background: CHARCOAL, aspectRatio: "9/19" }}>
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full" style={{ background: "#000" }} />
        <div className="w-full h-full rounded-[28px] overflow-hidden bg-white flex flex-col">
          <div className="h-7" style={{ background: RED }} />
          <div className="px-4 py-3 flex items-center gap-2 border-b border-zinc-200">
            <img src={BRAND.logo} alt="logo" className="w-7 h-7 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-black text-xs">{BRAND.name}</div>
              <div className="text-[8px] uppercase tracking-widest text-zinc-500">Marketplace</div>
            </div>
          </div>
          <div className="px-3 py-3 grid grid-cols-2 gap-2">
            <div className="border border-zinc-200 p-2">
              <div className="text-[8px] uppercase text-zinc-500">Opportunities</div>
              <div className="font-display font-black text-lg" style={{ color: RED }}>{cv("phone_stat_opps", "248")}</div>
            </div>
            <div className="border border-zinc-200 p-2">
              <div className="text-[8px] uppercase text-zinc-500">Vendors</div>
              <div className="font-display font-black text-lg" style={{ color: GOLD }}>{cv("phone_stat_vendors", "17")}</div>
            </div>
          </div>
          <div className="px-3 space-y-1.5">
            {BRANDS.slice(0, 4).map((b) => (
              <div key={b.key} className="flex items-center justify-between border border-zinc-200 px-2 py-1.5">
                <div className="text-[9px] font-medium truncate pr-2">{b.name} · {b.opportunityTypes?.[0]}</div>
                <Lucide.ChevronRight className="w-3 h-3 text-zinc-400 shrink-0" />
              </div>
            ))}
          </div>
          <div className="mt-auto px-3 pb-3 pt-2">
            <div className="w-full text-center text-[10px] font-semibold text-white py-2 rounded" style={{ background: RED }}>
              Apply Now
            </div>
          </div>
        </div>
      </div>
      <div
        className="absolute -inset-3 -z-10 rounded-[48px] opacity-20 blur-2xl"
        style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }}
      />
    </div>
  );
}

// ============================================================
// OPPORTUNITY TV PREVIEW — Big magnet to recruit new members
// ============================================================
function OpportunityTVPreview() {
  const navigate = useNavigate();
  const { t } = useT();
  const [promos, setPromos] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [featuredReady, setFeaturedReady] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/promos/feed?limit=4`)
      .then((r) => setPromos((r.data || []).slice(0, 4)))
      .catch(() => {});
  }, []);

  // Show skeleton briefly whenever the featured promo changes.
  useEffect(() => { setFeaturedReady(false); }, [activeIdx]);

  if (!promos.length) return null;

  const featured = promos[activeIdx] || promos[0];
  const videoSrc = featured.video_url?.startsWith("http") ? featured.video_url : `${BACKEND}${featured.video_url}`;

  return (
    <section id="opportunity-tv-preview" className="relative overflow-hidden border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: RED }} />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: GOLD }} />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white/10 backdrop-blur border border-white/15" data-testid="tv-preview-tag">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: GOLD }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: GOLD }} />
              </span>
              <span>{t("tv.live_badge")}</span>
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.05]">
              {t("tv.headline_1")}<br />
              <span style={{ color: GOLD }}>{t("tv.headline_2")}</span>
            </h2>
            <p className="mt-5 text-white/85 text-base sm:text-lg max-w-xl">{t("tv.sub")}</p>
          </div>
          <button
            onClick={() => navigate("/tv")}
            data-testid="tv-preview-cta-top"
            className="hidden sm:inline-flex items-center gap-2 text-white px-6 py-3 font-bold hover:opacity-90"
            style={{ background: RED }}
          >
            {t("tv.cta_watch")} <Lucide.Play className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8 items-start">
          {/* Featured phone-shaped video */}
          <div
            className="relative aspect-[9/16] w-full max-w-[420px] mx-auto lg:mx-0 bg-black overflow-hidden cursor-pointer group"
            onClick={() => navigate("/tv")}
            data-testid="tv-preview-featured"
          >
            {/* Loading skeleton — visible until the video has a first frame ready */}
            {!featuredReady && (
              <div className="absolute inset-0 grid place-items-center" data-testid="tv-preview-skeleton" style={{ background: "linear-gradient(135deg, #1a1a1d 0%, #09090b 70%)" }}>
                <div className="flex flex-col items-center gap-3 text-white/40">
                  <div className="w-12 h-12 grid place-items-center bg-white/5 border border-white/10">
                    <Lucide.Play className="w-5 h-5 fill-current" />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold animate-pulse">Loading video…</div>
                </div>
              </div>
            )}
            <video
              key={featured.promo_id}
              src={videoSrc}
              autoPlay muted loop playsInline
              preload="auto"
              onLoadedData={() => setFeaturedReady(true)}
              onCanPlay={() => setFeaturedReady(true)}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${featuredReady ? "opacity-100" : "opacity-0"}`}
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.85) 100%)" }} />
            <div className="absolute bottom-0 inset-x-0 p-5 text-white">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold mb-2">
                <span className="px-2 py-0.5 border border-white/30 bg-white/10 backdrop-blur">{featured.owner_role?.replace("_", " ")}</span>
                {featured.brand_tag && <span className="text-white/70">#{featured.brand_tag}</span>}
              </div>
              <h3 className="font-display font-black text-xl leading-tight">{featured.title}</h3>
              <div className="mt-2 flex items-center gap-3 text-xs text-white/70">
                <span className="inline-flex items-center gap-1"><Lucide.Heart className="w-3 h-3" /> {featured.likes || 0}</span>
                <span className="inline-flex items-center gap-1"><Lucide.Eye className="w-3 h-3" /> {featured.views || 0}</span>
              </div>
            </div>
            <div className="absolute top-4 right-4 w-12 h-12 grid place-items-center bg-white/15 backdrop-blur border border-white/20 group-hover:bg-white/25">
              <Lucide.Play className="w-5 h-5 text-white fill-current" />
            </div>
          </div>

          {/* Thumb grid + benefits */}
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tv-preview-thumbs">
              {promos.slice(0, 4).map((p, i) => {
                const src = p.video_url?.startsWith("http") ? p.video_url : `${BACKEND}${p.video_url}`;
                return (
                  <button
                    key={p.promo_id}
                    onClick={() => setActiveIdx(i)}
                    data-testid={`tv-preview-thumb-${i}`}
                    className={`relative aspect-[9/16] bg-black overflow-hidden border-2 transition-all ${i === activeIdx ? "border-[#E0AA3E]" : "border-transparent hover:border-white/40"}`}
                  >
                    {/* preload=metadata loads only the first frame (≈10-30 KB), not the full file.
                        Lazy-decode hint keeps initial paint snappy. */}
                    <video
                      src={src}
                      muted playsInline
                      preload="metadata"
                      className="absolute inset-0 w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.75) 100%)" }} />
                    <div className="absolute bottom-1.5 left-1.5 right-1.5 text-[9px] uppercase tracking-widest font-bold text-white line-clamp-2 leading-tight">
                      {p.brand_tag ? "#" + p.brand_tag : (p.title || "").slice(0, 28)}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: "Eye",       title: t("tv.benefit_1_title"), desc: t("tv.benefit_1_desc") },
                { icon: "Megaphone", title: t("tv.benefit_2_title"), desc: t("tv.benefit_2_desc") },
                { icon: "Rocket",    title: t("tv.benefit_3_title"), desc: t("tv.benefit_3_desc") },
              ].map((b, i) => (
                <div key={i} className="border border-white/15 bg-white/5 backdrop-blur p-4">
                  <div className="w-9 h-9 grid place-items-center text-white" style={{ background: i % 2 === 0 ? RED : GOLD }}>
                    <Icon name={b.icon} className="w-4 h-4" />
                  </div>
                  <div className="font-display font-black text-sm tracking-tight mt-3">{b.title}</div>
                  <div className="text-[11px] text-white/70 mt-1 leading-snug">{b.desc}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/tv")}
                data-testid="tv-preview-cta-watch"
                className="inline-flex items-center gap-2 text-white px-5 py-3 font-bold hover:opacity-90"
                style={{ background: RED }}
              >
                {t("tv.cta_watch")} <Lucide.Play className="w-4 h-4" />
              </button>
              <button
                onClick={() => startLogin("/post-promo")}
                data-testid="tv-preview-cta-post"
                className="inline-flex items-center gap-2 bg-white text-zinc-900 px-5 py-3 font-bold hover:bg-zinc-200"
              >
                {t("tv.cta_post")} <Lucide.Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// SPOTLIGHTS — Admin-managed "shout-out" calls for opportunities
// ============================================================
function IWantToStrip() {
  const navigate = useNavigate();
  const { t } = useT();
  const [cfg, setCfg] = useState({});
  const [pathways, setPathways] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND}/api/public/community`).then((r) => setCfg(r.data || {})).catch(() => {});
    axios.get(`${BACKEND}/api/public/pathways`).then((r) => setPathways(Array.isArray(r.data) ? r.data : [])).catch(() => setPathways([]));
  }, []);
  const num = (cfg.whatsapp_number || "+60137003381").replace(/\D/g, "");
  const openWA = (text) => {
    const tpl = encodeURIComponent(text || "Hi MIRESCO!");
    window.open(`https://wa.me/${num}?text=${tpl}`, "_blank", "noopener");
  };
  const accentColor = (a) => ({ red: RED, gold: GOLD, charcoal: CHARCOAL, green: "#0e7c66", blue: "#1e3a8a" }[a] || RED);
  const handlePathway = (p) => {
    if (p.action_type === "whatsapp") return openWA(p.wa_template || p.action_target || "Hi MIRESCO!");
    if (p.action_type === "external" && p.action_target) return window.open(p.action_target, "_blank", "noopener");
    if (p.action_type === "route" && p.action_target) return navigate(p.action_target);
    return openWA(p.wa_template || "Hi MIRESCO!");
  };
  return (
    <section className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-16" data-testid="i-want-strip">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> {t("iwant.tag")}</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-3 tracking-tight leading-[1.05]">{t("iwant.heading")} <span style={{ color: RED }}>{t("iwant.today")}</span></h2>
            <p className="mt-2 text-zinc-600 text-sm sm:text-base">{t("iwant.intro")}</p>
          </div>
        </div>
        {pathways.length === 0 ? (
          <div className="text-sm text-zinc-500 font-mono">{t("iwant.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pathways-grid">
            {pathways.map((p) => (
              <button
                key={p.pathway_id}
                data-testid={`pathway-${p.pathway_id}`}
                onClick={() => handlePathway(p)}
                className="text-left bg-white border border-zinc-200 p-5 hover:border-zinc-900 hover:shadow-[6px_6px_0_0_#09090b] transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 grid place-items-center text-white" style={{ background: accentColor(p.accent) }}>
                    {p.emoji ? <span className="text-xl leading-none">{p.emoji}</span> : <Icon name={p.icon} className="w-5 h-5" />}
                  </div>
                  <div className="font-display font-black text-base tracking-tight leading-tight">{p.title}</div>
                </div>
                {p.subtitle && <div className="mt-3 text-sm text-zinc-600">{p.subtitle}</div>}
                <div className="mt-4 text-[10px] uppercase tracking-widest font-semibold text-zinc-500 inline-flex items-center gap-1">
                  {p.action_type === "whatsapp" ? <Lucide.Phone className="w-3 h-3" /> : <Lucide.ArrowRight className="w-3 h-3" />}
                  {p.action_type === "whatsapp" ? t("iwant.wa_us") : t("iwant.explore")}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================
// FEATURED BUSINESS OPPORTUNITIES (CMS-driven — from /api/public/business-opportunities)
// ============================================================
function FeaturedOpportunities() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const { t } = useT();
  useEffect(() => {
    axios
      .get(`${BACKEND}/api/public/business-opportunities?limit=6`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-16" data-testid="featured-opps">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> {t("opps.tag")}</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-3 tracking-tight leading-[1.05]">{t("opps.heading_live_pre")} <span style={{ color: RED }}>{t("opps.heading_live_hi")}</span></h2>
            <p className="mt-2 text-zinc-600 text-sm sm:text-base">{t("opps.intro_live")}</p>
          </div>
          <button
            onClick={() => navigate("/opportunities-directory")}
            data-testid="featured-see-all"
            className="text-sm font-semibold hover:opacity-80 inline-flex items-center gap-1"
            style={{ color: RED }}
          >
            {t("opps.see_all")} <Lucide.ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((o) => (
            <button
              key={o.opportunity_id}
              onClick={() => navigate(`/opportunity/${o.slug}`)}
              data-testid={`featured-card-${o.slug}`}
              className="text-left bg-white border border-zinc-200 hover:border-zinc-900 hover:shadow-[6px_6px_0_0_#09090b] transition-all"
            >
              <div className="aspect-video bg-zinc-100 relative overflow-hidden">
                {o.hero_image_url ? (
                  <img src={o.hero_image_url} alt={o.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <div className="w-full h-full grid place-items-center" style={{ background: `linear-gradient(135deg, ${RED} 0%, #7c0a1e 100%)` }}>
                    <span className="text-white font-display font-black text-3xl tracking-tight">{o.brand || "MIRESCO"}</span>
                  </div>
                )}
                {o.featured && (
                  <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 font-black uppercase tracking-widest" style={{ background: GOLD, color: CHARCOAL }}>Featured</span>
                )}
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{o.category_slug || "—"}</div>
                <div className="mt-1 font-display font-black text-lg tracking-tight leading-tight">{o.title}</div>
                {o.tagline && <div className="mt-1 text-sm text-zinc-600 line-clamp-2">{o.tagline}</div>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(o.opportunity_types || []).slice(0, 4).map((t) => (
                    <span key={t} className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 border border-zinc-300">{t}</span>
                  ))}
                </div>
                <div className="mt-3 text-xs text-zinc-600 font-semibold truncate">{o.investment_required || "—"}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// SUCCESS STORIES CAROUSEL — social proof, homepage conversion booster
// ============================================================
function SuccessStoriesCarousel() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const railRef = useRef(null);
  const { t } = useT();

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/public/success-stories?limit=12`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  const scrollBy = (dir) => {
    const el = railRef.current;
    if (!el) return;
    const card = el.querySelector('[data-testid^="success-card-"]');
    const step = (card?.offsetWidth || 320) + 20; // gap-5 ≈ 20px
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const fmtIncome = (n) => {
    if (!n) return "";
    if (n >= 1000) return `RM ${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k/mo`;
    return `RM ${n}/mo`;
  };

  if (items.length === 0) return null;

  return (
    <section id="success-stories-carousel" className="border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-16" data-testid="success-carousel">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1" style={{ background: `${GOLD}20`, color: GOLD }}>
              <Lucide.Sparkles className="w-3 h-3" /> {t("success.tag")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-3 tracking-tight leading-[1.05]">
              {t("success.heading")} <span style={{ color: GOLD }}>MIRESCO</span> {t("success.today")}
            </h2>
            <p className="mt-2 text-white/70 text-sm sm:text-base max-w-2xl">
              {t("success.intro")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scrollBy(-1)}
              aria-label="Previous"
              className="w-10 h-10 border border-white/20 hover:bg-white/10 grid place-items-center transition-colors"
              data-testid="success-carousel-prev"
            >
              <Lucide.ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              aria-label="Next"
              className="w-10 h-10 border border-white/20 hover:bg-white/10 grid place-items-center transition-colors"
              data-testid="success-carousel-next"
            >
              <Lucide.ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate("/success-stories")}
              data-testid="success-carousel-see-all"
              className="ml-2 text-sm font-semibold hover:opacity-80 inline-flex items-center gap-1"
              style={{ color: GOLD }}
            >
              {t("success.see_all")} <Lucide.ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={railRef}
          className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-5 px-5 lg:mx-0 lg:px-0"
          data-testid="success-carousel-rail"
        >
          {items.map((s) => (
            <button
              key={s.story_id || s.slug}
              onClick={() => navigate("/success-stories")}
              data-testid={`success-card-${s.slug}`}
              className="text-left shrink-0 w-[300px] sm:w-[340px] snap-start bg-white text-zinc-900 border border-white/10 hover:border-white/40 transition-all overflow-hidden group"
            >
              <div className="aspect-[4/5] bg-zinc-800 relative overflow-hidden">
                {s.hero_image_url ? (
                  <img
                    src={s.hero_image_url}
                    alt={s.name || s.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center" style={{ background: `linear-gradient(135deg, ${RED} 0%, #7c0a1e 100%)` }}>
                    <Lucide.User className="w-12 h-12 text-white/40" />
                  </div>
                )}
                {/* Bottom gradient overlay */}
                <div className="absolute inset-x-0 bottom-0 h-2/3" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)" }} />
                {/* Income pill */}
                {s.monthly_income_myr > 0 && (
                  <div className="absolute top-3 right-3 text-[11px] font-black px-2.5 py-1 tracking-tight" style={{ background: GOLD, color: CHARCOAL }} data-testid={`success-card-${s.slug}-income`}>
                    {fmtIncome(s.monthly_income_myr)}
                  </div>
                )}
                {s.featured && (
                  <div className="absolute top-3 left-3 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 border" style={{ background: "rgba(0,0,0,0.6)", color: GOLD, borderColor: GOLD }}>
                    <Lucide.Star className="w-3 h-3 inline mr-1" fill={GOLD} strokeWidth={0} />{t("success.featured")}
                  </div>
                )}
                {/* Name / role overlay */}
                <div className="absolute bottom-0 inset-x-0 p-4 text-white">
                  <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{s.story_type || "success story"}</div>
                  <div className="font-display font-black text-lg tracking-tight leading-tight mt-1">{s.name || t("success.default_name")}</div>
                  <div className="text-xs opacity-90">{s.role}{s.role && s.location ? " · " : ""}{s.location}</div>
                </div>
              </div>
              <div className="p-4">
                <div className="font-display font-black text-base tracking-tight leading-snug line-clamp-2 min-h-[2.75rem]">{s.title}</div>
                {s.quote && (
                  <div className="mt-2 text-sm text-zinc-600 italic line-clamp-3">&ldquo;{s.quote}&rdquo;</div>
                )}
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: RED }}>
                  {t("success.read_story")} <Lucide.ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// SPOTLIGHTS — Admin-managed "shout-out" calls for opportunities
// ============================================================
function Spotlights() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/spotlights`)
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, []);

  if (!items.length) return null;

  const accentMap = {
    red: RED,
    gold: GOLD,
    charcoal: CHARCOAL,
  };

  return (
    <section id="spotlights" className="border-b border-zinc-200 relative overflow-hidden" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="spotlights-section">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white border border-zinc-300" data-testid="spotlights-tag">
              <Lucide.Megaphone className="w-3.5 h-3.5" style={{ color: RED }} /> Spotlight
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight leading-[1.05]">
              Now recruiting — <span style={{ color: RED }}>spotlight calls.</span>
            </h2>
            <p className="mt-3 text-zinc-600 text-base sm:text-lg">
              Time-sensitive opportunities our partner brands want filled fast. Apply directly or share with someone who fits.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="spotlights-grid">
          {items.map((s) => {
            const accent = accentMap[s.accent] || RED;
            return (
              <div
                key={s.spotlight_id}
                className="relative bg-white border border-zinc-200 p-6 lg:p-8 hover:border-zinc-900 hover:shadow-[8px_8px_0_0_#09090b] transition-all"
                data-testid={`spotlight-${s.spotlight_id}`}
              >
                <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: accent }} />
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 text-white" style={{ background: accent }}>
                    <Lucide.Megaphone className="w-3 h-3" /> {s.audience || "Open call"}
                  </span>
                  {s.brand_tag ? (
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">#{s.brand_tag}</span>
                  ) : null}
                </div>
                <h3 className="font-display font-black text-xl lg:text-2xl tracking-tight leading-tight">{s.title}</h3>
                {s.subtitle ? (
                  <div className="text-sm font-semibold mt-2" style={{ color: accent }}>{s.subtitle}</div>
                ) : null}
                {s.body ? (
                  <p className="text-sm text-zinc-700 mt-3 leading-snug">{s.body}</p>
                ) : null}
                <button
                  onClick={() => {
                    const url = s.cta_url || "/opportunities";
                    if (/^https?:/i.test(url)) {
                      window.open(url, "_blank", "noopener");
                    } else {
                      // Internal routes like /opportunities are auth-gated; route via startLogin
                      // so anonymous visitors land back on the page after sign-in.
                      startLogin(url);
                    }
                  }}
                  data-testid={`spotlight-cta-${s.spotlight_id}`}
                  className="mt-5 inline-flex items-center gap-2 text-white px-5 py-2.5 font-bold text-sm hover:opacity-90"
                  style={{ background: accent }}
                >
                  {s.cta_label || "Apply now"} <Lucide.ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// THREE PATHWAYS
// ============================================================
function WhyJoinUs() {
  const { t } = useT();
  const navigate = useNavigate();
  const benefits = [
    { icon: "Wallet",     title: t("why.b1_title"), desc: t("why.b1_desc"), accent: RED },
    { icon: "Smartphone", title: t("why.b2_title"), desc: t("why.b2_desc"), accent: GOLD },
    { icon: "Users",      title: t("why.b3_title"), desc: t("why.b3_desc"), accent: CHARCOAL },
    { icon: "ShieldCheck",title: t("why.b4_title"), desc: t("why.b4_desc"), accent: RED },
    { icon: "Rocket",     title: t("why.b5_title"), desc: t("why.b5_desc"), accent: GOLD },
    { icon: "Globe2",     title: t("why.b6_title"), desc: t("why.b6_desc"), accent: CHARCOAL },
  ];
  return (
    <section id="why-join" className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="tag" data-testid="why-tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> {t("why.tag")}</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight leading-[1.05]">{t("why.heading")}</h2>
            <p className="mt-4 text-zinc-600 text-base sm:text-lg">{t("why.intro")}</p>
          </div>
          <button
            onClick={() => startLogin("/dashboard")}
            data-testid="why-cta"
            className="inline-flex items-center gap-2 text-white px-6 py-3 font-bold hover:opacity-90"
            style={{ background: RED }}
          >
            {t("why.cta")} <Lucide.ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="why-grid">
          {benefits.map((b) => (
            <div key={b.title} className="border border-zinc-200 p-6 hover:border-[#C8102E] hover:shadow-[6px_6px_0_0_#C8102E] transition-all" data-testid={`why-card-${b.title.toLowerCase().slice(0, 12).replace(/\s+/g, '-')}`}>
              <div className="w-12 h-12 grid place-items-center text-white" style={{ background: b.accent }}>
                <Icon name={b.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-display font-black text-lg mt-5 tracking-tight">{b.title}</h3>
              <p className="text-sm text-zinc-600 mt-2 leading-snug">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pathways() {
  const navigate = useNavigate();
  const { t } = useT();
  return (
    <section id="pathways" className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag" data-testid="pathways-tag">
              <span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> {t("pathways.tag")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">
              {t("pathways.heading")}
            </h2>
          </div>
          <p className="max-w-md text-zinc-600">{t("pathways.intro")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="legacy-pathways-grid">
          {PATHWAYS.map((p, i) => (
            <div
              key={p.key}
              data-testid={`legacy-pathway-${p.key}`}
              className="relative p-7 bg-white border-2 transition-all hover:shadow-[8px_8px_0_0_currentColor] flex flex-col"
              style={{ borderColor: p.color, color: p.color }}
            >
              <div className="overline" style={{ color: p.color }}>0{i + 1} · {p.tag}</div>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-12 h-12 grid place-items-center text-white" style={{ background: p.color }}>
                  <Icon name={p.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-display font-black text-2xl tracking-tight text-zinc-900">{p.title}</h3>
              </div>
              <p className="text-sm text-zinc-700 mt-4 flex-grow">{p.desc}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {p.examples.map((e) => (
                  <span key={e} className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 bg-zinc-100 text-zinc-700">
                    {e}
                  </span>
                ))}
              </div>
              <button
                onClick={() => { trackEvent("pathway_" + p.key); handleCta(navigate, p); }}
                data-testid={`pathway-cta-${p.key}`}
                className="mt-6 inline-flex items-center justify-center gap-2 text-white px-5 py-3 font-semibold hover:opacity-90 transition-opacity"
                style={{ background: p.color }}
              >
                {p.cta} <Lucide.ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FEATURED OPPORTUNITIES (6 brands)
// ============================================================
function BrandImage({ brand }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="h-48 relative overflow-hidden" style={{ background: brand.fallback }}>
      {brand.image && !failed && (
        <img
          src={brand.image}
          alt={brand.name}
          loading="lazy"
          onError={() => setFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)" }} />
      <div className="absolute bottom-3 left-4 font-display font-black text-5xl text-white drop-shadow">
        {brand.initial}
      </div>
      {brand.badge && (
        <div
          className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-semibold px-2 py-1"
          style={{ background: GOLD, color: CHARCOAL }}
        >
          {brand.badge}
        </div>
      )}
    </div>
  );
}

function Opportunities() {
  const navigate = useNavigate();
  const { t } = useT();
  return (
    <section id="opportunities" className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> {t("opps.tag")}</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">{t("opps.heading")}</h2>
          </div>
          <p className="max-w-md text-zinc-600">{t("opps.intro")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="opportunities-grid">
          {BRANDS.map((b) => (
            <div
              key={b.key}
              data-testid={`brand-${b.key}`}
              className="group bg-white border border-zinc-200 overflow-hidden hover:border-[#C8102E] hover:shadow-[8px_8px_0_0_#C8102E] transition-all flex flex-col"
            >
              <BrandImage brand={b} />
              <div className="p-5 flex flex-col flex-grow">
                <h3 className="font-display font-black text-lg tracking-tight">{b.name}</h3>
                <div className="text-[11px] uppercase tracking-widest font-semibold mt-1" style={{ color: RED }}>
                  {b.tagline}
                </div>
                <p className="text-sm text-zinc-600 mt-3 flex-grow">{b.desc}</p>
                {b.opportunityTypes?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {b.opportunityTypes.map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 bg-zinc-100 text-zinc-700">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => {
                    trackEvent("brand_click_" + b.key);
                    if (b.showcaseUrl) navigate(b.showcaseUrl);
                    else startLogin("/opportunities");
                  }}
                  data-testid={`brand-cta-${b.key}`}
                  className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold group-hover:text-[#C8102E]"
                >
                  {b.showcaseUrl ? t("opps.view_showcase") : t("opps.apply_now")} <Lucide.ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// ABOUT
// ============================================================
function About() {
  return (
    <section id="about" className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-center">
          <div className="lg:col-span-7">
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> {ABOUT.tag}</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">{ABOUT.heading}</h2>
            <p className="mt-5 text-zinc-600 text-base sm:text-lg max-w-xl">{ABOUT.body}</p>
          </div>
          {ABOUT.image && (
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden border border-zinc-200" style={{ aspectRatio: "4/3" }}>
                <img
                  src={ABOUT.image}
                  alt={ABOUT.imageAlt}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-widest font-semibold px-2 py-1" style={{ background: GOLD, color: CHARCOAL }}>
                  Our Community
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="user-types-grid">
          {ABOUT.userTypes.map((u, i) => (
            <div
              key={u.key}
              data-testid={`user-type-${u.key}`}
              className="border border-zinc-200 p-6 hover:border-[#C8102E] transition-all hover:shadow-[6px_6px_0_0_#C8102E] bg-white"
            >
              <div className="w-12 h-12 grid place-items-center text-white" style={{ background: i % 2 === 0 ? RED : CHARCOAL }}>
                <Icon name={u.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-display font-black text-lg mt-5 tracking-tight">{u.label}</h3>
              <p className="text-sm text-zinc-600 mt-2 leading-snug">{u.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// ENTREPRENEUR JOURNEY (6-stage roadmap)
// ============================================================
function EntrepreneurJourney() {
  return (
    <section id="journey" className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="text-center mb-12">
          <div className="tag inline-flex"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Entrepreneur Journey</div>
          <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">From RM 0 to Business Leader.</h2>
          <p className="max-w-2xl mx-auto text-zinc-600 mt-4">
            Six stages. One platform. We help you progress from your first ringgit earned to leading your own ecosystem.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="journey-grid">
          {JOURNEY.map((s, i) => (
            <div
              key={s.key}
              data-testid={`journey-stage-${s.stage}`}
              className="relative bg-white border-2 p-5 flex flex-col"
              style={{ borderColor: i === 0 ? RED : "#E4E4E7" }}
            >
              <div className="overline" style={{ color: RED }}>Stage 0{s.stage}</div>
              <div className="w-10 h-10 grid place-items-center text-white mt-3" style={{ background: i % 2 === 0 ? RED : CHARCOAL }}>
                <Icon name={s.icon} className="w-5 h-5" />
              </div>
              <h3 className="font-display font-black text-lg mt-4 tracking-tight">{s.title}</h3>
              <p className="text-xs text-zinc-600 mt-2 leading-snug flex-grow">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// BUSINESS CATEGORIES
// ============================================================
function BusinessCategories() {
  const navigate = useNavigate();
  return (
    <section id="categories" className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Categories</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Browse by industry.</h2>
          </div>
          <p className="max-w-md text-zinc-600">
            Find opportunities and businesses in the category that fits you.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="categories-grid">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => { trackEvent("category_" + c.key); startLogin("/opportunities"); }}
              data-testid={`category-${c.key}`}
              className="text-left bg-white border border-zinc-200 hover:border-[#C8102E] hover:shadow-[6px_6px_0_0_#C8102E] transition-all p-5"
            >
              <div className="w-10 h-10 grid place-items-center text-white" style={{ background: RED }}>
                <Icon name={c.icon} className="w-5 h-5" />
              </div>
              <h3 className="font-display font-black text-sm mt-3 tracking-tight leading-tight">{c.title}</h3>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// PROGRAMS
// ============================================================
function Programs() {
  return (
    <section id="programs" className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Programs</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Our programs</h2>
          </div>
          <p className="max-w-md text-zinc-600">
            Six structured pathways to commercial growth — pick the one that matches your goals.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="programs-grid">
          {PROGRAMS.map((p) => (
            <div
              key={p.key}
              data-testid={`program-${p.key}`}
              className="border border-zinc-200 p-6 hover:border-[#C8102E] hover:shadow-[6px_6px_0_0_#C8102E] transition-all bg-white"
            >
              <div className="w-12 h-12 grid place-items-center text-white" style={{ background: RED }}>
                <Icon name={p.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-display font-black text-xl mt-5 tracking-tight">{p.title}</h3>
              <p className="text-sm text-zinc-600 mt-2 leading-snug">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// MEMBERSHIP
// ============================================================
function Membership() {
  return (
    <section id="membership" className="border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag border-white/20" style={{ color: "white" }}>
              <span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Membership
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Pick your tier.</h2>
          </div>
          <p className="max-w-md text-zinc-300">
            Start free, upgrade when you&apos;re ready. Cancel anytime. All tiers include access to the MIRESCO BIZ ecosystem.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="membership-grid">
          {TIERS.map((t) => (
            <div
              key={t.key}
              data-testid={`tier-${t.key}`}
              className={`relative p-7 transition-all ${t.popular ? "lg:-translate-y-2 shadow-2xl" : ""}`}
              style={{ background: "white", color: CHARCOAL, border: `2px solid ${t.popular ? GOLD : "transparent"}` }}
            >
              {t.popular && (
                <div className="absolute -top-3 left-7 text-[10px] font-bold uppercase tracking-widest px-3 py-1" style={{ background: GOLD, color: CHARCOAL }}>
                  Most Popular
                </div>
              )}
              <div className="w-12 h-12 grid place-items-center text-white" style={{ background: t.accent }}>
                <Icon name={t.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-display font-black text-2xl mt-5 tracking-tight">{t.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display font-black text-4xl tracking-tight">{t.price}</span>
                <span className="text-sm text-zinc-500">{t.per}</span>
              </div>
              <ul className="mt-6 space-y-2">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                    <Lucide.Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: t.accent }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { trackEvent("tier_click_" + t.key); startLogin("/membership"); }}
                data-testid={`tier-cta-${t.key}`}
                className="mt-7 w-full text-sm font-semibold py-3 transition-opacity hover:opacity-90"
                style={{ background: t.accent, color: t.key === "elite" ? CHARCOAL : "white" }}
              >
                Get {t.name.split(" ")[0]}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// COVERAGE
// ============================================================
function Coverage() {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <section id="coverage" className="border-b border-zinc-200 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> {COVERAGE.tag}</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">
              Available in all <span style={{ color: RED }}>16 states</span> across Malaysia.
            </h2>
            <p className="mt-5 text-zinc-600">{COVERAGE.body}</p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="key-states">
              {COVERAGE.keyStates.map((s) => (
                <span key={s} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border" style={{ borderColor: RED, color: RED }}>
                  {s}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-2xl">
              {COVERAGE.image && !imgFailed && (
                <img
                  src={COVERAGE.image}
                  alt={COVERAGE.imageAlt}
                  loading="lazy"
                  onError={() => setImgFailed(true)}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${RED}E6 0%, ${CHARCOAL}E6 100%)` }} />
              <div className="relative p-8">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-white/90">
                  <Lucide.Globe className="w-4 h-4" /> Malaysia · 16 states & FTs
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-6" data-testid="states-grid">
                  {COVERAGE.allStates.map((s) => (
                    <div key={s} data-testid={`state-${s.toLowerCase().replace(/\s/g,'-')}`}
                         className="bg-white/95 backdrop-blur border border-white/40 px-3 py-3 flex items-center gap-2">
                      <Lucide.MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: RED }} />
                      <span className="text-xs font-semibold truncate">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// COMING SOON (future features placeholder)
// ============================================================
function ComingSoon() {
  return (
    <section className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Roadmap</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Coming soon</h2>
          </div>
          <p className="max-w-md text-zinc-600">
            What we&apos;re building next — live commerce, AI matching, regional expansion.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="coming-soon-grid">
          {COMING_SOON.map((c) => (
            <div
              key={c.key}
              data-testid={`coming-soon-${c.key}`}
              className="bg-white border border-zinc-200 p-5 relative"
            >
              <div className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5" style={{ background: GOLD, color: CHARCOAL }}>
                Soon
              </div>
              <Icon name={c.icon} className="w-6 h-6" style={{ color: RED }} />
              <h3 className="font-display font-black text-sm mt-3 tracking-tight leading-tight">{c.title}</h3>
              <p className="text-[11px] text-zinc-600 mt-1.5 leading-snug">{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// APP STORE BADGE
// ============================================================
function AppBadge({ store, dataTestId }) {
  const cfg = APP_STORES[store];
  const isApple = store === "apple";
  const comingSoon = cfg?.comingSoon;
  return (
    <div
      data-testid={dataTestId}
      className="relative inline-flex items-center gap-3 px-5 py-3 rounded-xl cursor-default select-none"
      style={{ background: CHARCOAL, color: "white" }}
      title={comingSoon ? "Coming Soon" : ""}
    >
      {isApple ? <Lucide.Apple className="w-8 h-8" /> : <Lucide.Play className="w-8 h-8" />}
      <div className="text-left leading-tight">
        <div className="text-[10px] uppercase tracking-widest text-zinc-300">
          {comingSoon ? (isApple ? "Coming soon on the" : "Coming soon on") : (isApple ? "Download on the" : "Get it on")}
        </div>
        <div className="font-display font-black text-base">
          {isApple ? "App Store" : "Google Play"}
        </div>
      </div>
      {comingSoon && (
        <span className="absolute -top-2 -right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: GOLD, color: CHARCOAL }}>
          Coming Soon
        </span>
      )}
    </div>
  );
}

// ============================================================
// FINAL CTA
// ============================================================
function FinalCTA() {
  return (
    <section className="relative overflow-hidden" style={{ background: RED, color: "white" }}>
      <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 80% 50%, ${GOLD} 0%, transparent 60%)` }} />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 text-center">
        <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight" data-testid="final-cta-heading">
          {FINAL_CTA.heading}
        </h2>
        <p className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl mx-auto">{FINAL_CTA.body}</p>

        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => { trackEvent("final_cta_start"); startLogin("/opportunities"); }}
            data-testid="final-cta-start"
            className="inline-flex items-center gap-2 bg-white px-6 py-3 font-semibold hover:bg-zinc-100 transition-colors"
            style={{ color: RED }}
          >
            Start Earning <Lucide.ArrowRight className="w-4 h-4" />
          </button>
          <AppBadge store="apple"  dataTestId="final-appstore" />
          <AppBadge store="google" dataTestId="final-playstore" />
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FOOTER
// ============================================================
function Footer() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({ whatsapp_url: "", telegram_url: "" });
  useEffect(() => {
    axios.get(`${BACKEND}/api/public/community`)
      .then(({ data }) => setCfg(data))
      .catch(() => {});
  }, []);

  return (
    <footer style={{ background: CHARCOAL, color: "white" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt={BRAND.name} className="w-12 h-12 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-black text-lg">{BRAND.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">{BRAND.tagline}</div>
            </div>
          </div>
          <p className="mt-5 text-sm text-zinc-400 max-w-sm">{FOOTER.blurb}</p>
        </div>

        <div className="md:col-span-3">
          <div className="overline text-zinc-500">Explore</div>
          <ul className="mt-4 space-y-2 text-sm">
            {NAV_LINKS.map((n) => {
              const isInternal = n.href.startsWith("/");
              return (
                <li key={n.href}>
                  {isInternal
                    ? <button onClick={() => navigate(n.href)} className="hover:text-white text-zinc-300">{n.label}</button>
                    : <a href={n.href} className="hover:text-white text-zinc-300">{n.label}</a>}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline text-zinc-500">Contact</div>
          <ul className="mt-4 space-y-3 text-sm text-zinc-300">
            <li className="flex items-center gap-2">
              <Lucide.Mail className="w-4 h-4" />
              <a href={`mailto:${FOOTER.email}`} className="hover:text-white" data-testid="footer-email">
                {FOOTER.email}
              </a>
            </li>
            {cfg.whatsapp_url && (
              <li>
                <a href={cfg.whatsapp_url} target="_blank" rel="noreferrer" data-testid="footer-whatsapp"
                   className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold" style={{ background: "#25D366", color: "white" }}>
                  <Lucide.Phone className="w-4 h-4" /> WhatsApp Business
                </a>
              </li>
            )}
            {cfg.telegram_url && (
              <li>
                <a href={cfg.telegram_url} target="_blank" rel="noreferrer" data-testid="footer-telegram"
                   className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold" style={{ background: "#229ED9", color: "white" }}>
                  <Lucide.Send className="w-4 h-4" /> Telegram Channel
                </a>
              </li>
            )}
          </ul>

          <div className="overline text-zinc-500 mt-7">Follow</div>
          <div className="mt-4 flex items-center gap-3">
            {FOOTER.socials.map((s) => (
              <a key={s.key} href={s.url} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`social-${s.key}`}
                 className="w-9 h-9 grid place-items-center border border-white/20 hover:border-white text-zinc-300 hover:text-white">
                <Icon name={s.icon} className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <span>© {new Date().getFullYear()} {FOOTER.copyrightOwner}. All rights reserved.</span>
          <span className="font-mono">Phase 1 · PWA</span>
        </div>
      </div>
    </footer>
  );
}

// ============================================================
// MAIN
// ============================================================
export default function Landing() {
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    try {
      const k = "miresco_homepage_visit_fired";
      if (!sessionStorage.getItem(k)) {
        axios.post(`${BACKEND}/api/metrics/event`, { event: "homepage_visit" }).catch(() => {});
        sessionStorage.setItem(k, "1");
      }
    } catch (e) { /* sessionStorage unavailable in private mode — skip dedupe */ }

    // Capture ?ref=CODE from URL and remember it for lead attribution across pages.
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get("ref");
      if (ref && /^[A-Z0-9]{4,12}$/i.test(ref)) {
        localStorage.setItem("miresco_ref", ref.toUpperCase());
        axios.post(`${BACKEND}/api/referral/track/${ref.toUpperCase()}`).catch(() => {});
      }
    } catch { /* ignore */ }

    axios.get(`${BACKEND}/api/public/content`)
      .then((r) => setOverrides(r.data || {}))
      .catch(() => {});
  }, []);

  return (
    <ContentCtx.Provider value={overrides}>
      <div className="min-h-screen bg-white text-[#09090b]">
        <Header />
        <Hero />
        <IWantToStrip />
        <FeaturedOpportunities />
        <SuccessStoriesCarousel />
        <OpportunityTVPreview />
        <Spotlights />
        <WhyJoinUs />
        <Pathways />
        <EntrepreneurJourney />
        <Opportunities />
        <BusinessCategories />
        <About />
        <Programs />
        <Membership />
        <Coverage />
        <ComingSoon />
        <FinalCTA />
        <Footer />
      </div>
    </ContentCtx.Provider>
  );
}
